import { Check, CornerDownRight, Group, LayoutGrid, Plus, Trash2, Ungroup, Wand2, X, ArrowUpDown } from "lucide-react";
import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import { opKind } from "@/editor/types";
import { getNodeProp } from "@/editor/treeOps";
import type {
  AddNodeOp,
  ApplyRecipeOp,
  ComponentTree,
  MoveNodeOp,
  ProposalOp,
  RemoveNodeOp,
  SetPropOp,
  UnwrapNodeOp,
  WrapNodesOp,
} from "@/editor/types";
import { reviewProposal, type BuilderProposal } from "./useBuilder";

/**
 * One AI proposal rendered as a solid card in the conversation: rationale,
 * a per-op summary with a validity badge (the design system rejects ops it
 * can't express), and a single quiet "Apply" action. Applying writes only the
 * valid subset to the working tree — the preview then re-renders. No coral
 * here; coral is reserved for the single Publish action.
 */
export function ProposalCard({
  proposal,
  tree,
  index,
  onApply,
}: {
  proposal: BuilderProposal;
  tree: ComponentTree | null;
  index: number;
  onApply: (p: BuilderProposal) => void;
}) {
  const rows = reviewProposal(tree, proposal);
  const validCount = rows.filter((r) => r.ok).length;
  const total = rows.length;
  const nothingValid = total === 0 || validCount === 0;

  return (
    <Card
      className="animate-fade-up p-4"
      style={{ animationDelay: `${index * 40}ms` }}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-btn bg-paper-2 text-ink dark:bg-white/5">
          <Wand2 className="h-[16px] w-[16px]" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="eyebrow !text-subtle">Proposal</p>
            {proposal.applied && (
              <span className="inline-flex items-center gap-1 rounded-chip bg-paper-2 px-1.5 py-0.5 text-[10px] font-medium text-muted dark:bg-white/10">
                <Check className="h-3 w-3" strokeWidth={2} />
                Applied
              </span>
            )}
          </div>
          <p className="mt-0.5 text-[14px] leading-relaxed text-ink">
            {proposal.rationale}
          </p>

          {total > 0 ? (
            <ul className="mt-3 space-y-1.5">
              {rows.map((r, i) => (
                <li key={i} className="flex items-baseline gap-2 text-[12px]">
                  <span
                    className={cn(
                      "mt-px shrink-0",
                      r.ok ? "text-accent" : "text-subtle",
                    )}
                    aria-label={r.ok ? "valid" : "skipped"}
                  >
                    {r.ok ? (
                      <Check className="h-3.5 w-3.5" strokeWidth={2} />
                    ) : (
                      <X className="h-3.5 w-3.5" strokeWidth={2} />
                    )}
                  </span>
                  <span className="min-w-0 flex-1 leading-snug">
                    <OpSummary op={r.op} tree={tree} />
                    {!r.ok && r.reason && (
                      <span className="text-subtle"> · {r.reason}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-2 text-[12px] italic text-subtle">
              No applicable changes — Nara couldn't express this within the design
              system.
            </p>
          )}

          {total > 0 && validCount < total && (
            <p className="mt-2 text-[11px] text-subtle">
              Apply will make {validCount} of {total} changes; the rest are skipped.
            </p>
          )}

          <div className="mt-3 flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onApply(proposal)}
              disabled={nothingValid || proposal.applied}
            >
              <Check className="h-3.5 w-3.5" strokeWidth={1.75} />
              {proposal.applied
                ? "Applied"
                : validCount === total || total === 0
                  ? "Apply"
                  : `Apply ${validCount}`}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}

/** Compact, kind-aware one-line summary of an op. */
function OpSummary({ op, tree }: { op: ProposalOp; tree: ComponentTree | null }) {
  const kind = opKind(op);
  if (kind === "set_prop") {
    const o = op as SetPropOp;
    const before = tree ? getNodeProp(tree, o.node_id, o.prop) : undefined;
    return (
      <>
        <span className="font-mono text-[11px] text-muted">
          {o.node_id}.{o.prop}
        </span>{" "}
        <span className="text-subtle line-through">{stringify(before)}</span>{" "}
        <span className="text-ink-strong">→ {stringify(o.value)}</span>
      </>
    );
  }
  if (kind === "add_node") {
    const o = op as AddNodeOp;
    return (
      <>
        <Plus className="mr-1 inline h-3 w-3 align-[-1px] text-muted" strokeWidth={2} />
        <span className="text-ink">
          new <span className="font-medium text-ink-strong">{o.component}</span>
        </span>{" "}
        <span className="font-mono text-[11px] text-subtle">→ {o.parent_node_id}</span>
      </>
    );
  }
  if (kind === "remove_node") {
    const o = op as RemoveNodeOp;
    return (
      <>
        <Trash2 className="mr-1 inline h-3 w-3 align-[-1px] text-muted" strokeWidth={1.75} />
        <span className="text-ink">remove</span>{" "}
        <span className="font-mono text-[11px] text-subtle">{o.node_id}</span>
      </>
    );
  }
  if (kind === "apply_recipe") {
    const o = op as ApplyRecipeOp;
    return (
      <>
        <LayoutGrid className="mr-1 inline h-3 w-3 align-[-1px] text-muted" strokeWidth={1.75} />
        <span className="text-ink">restructure to</span>{" "}
        <span className="font-medium text-ink-strong">{o.recipe_id}</span>{" "}
        <span className="text-subtle">layout</span>
      </>
    );
  }
  if (kind === "wrap_nodes") {
    const o = op as WrapNodesOp;
    return (
      <>
        <Group className="mr-1 inline h-3 w-3 align-[-1px] text-muted" strokeWidth={1.75} />
        <span className="text-ink">
          group {o.node_ids.length} block{o.node_ids.length === 1 ? "" : "s"} into
        </span>{" "}
        <span className="font-medium text-ink-strong">{o.component}</span>
      </>
    );
  }
  if (kind === "unwrap_node") {
    const o = op as UnwrapNodeOp;
    return (
      <>
        <Ungroup className="mr-1 inline h-3 w-3 align-[-1px] text-muted" strokeWidth={1.75} />
        <span className="text-ink">ungroup</span>{" "}
        <span className="font-mono text-[11px] text-subtle">{o.node_id}</span>
      </>
    );
  }
  const o = op as MoveNodeOp;
  if (o.to_parent_id !== undefined) {
    return (
      <>
        <CornerDownRight className="mr-1 inline h-3 w-3 align-[-1px] text-muted" strokeWidth={1.75} />
        <span className="text-ink">move</span>{" "}
        <span className="font-mono text-[11px] text-subtle">{o.node_id}</span>{" "}
        <span className="text-ink">into</span>{" "}
        <span className="font-mono text-[11px] text-subtle">{o.to_parent_id}</span>
      </>
    );
  }
  return (
    <>
      <ArrowUpDown className="mr-1 inline h-3 w-3 align-[-1px] text-muted" strokeWidth={1.75} />
      <span className="text-ink">move {o.delta && o.delta < 0 ? "up" : "down"}</span>{" "}
      <span className="font-mono text-[11px] text-subtle">{o.node_id}</span>
    </>
  );
}

function stringify(v: unknown): string {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "string") return v.length > 40 ? v.slice(0, 40) + "…" : v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
